import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Clock, CheckCircle, XCircle, Monitor, Smartphone, 
  AlertTriangle, ChevronDown, FileSearch, Table, RotateCcw,
  ArrowRight, Timer
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  PLAYING: 'playing',
  REVIEW: 'review',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you&apos;re taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Best for tables</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// ===== DATA GENERATORS =====

// UK Station/Location names for manifest data
const LOCATIONS = [
  'Birmingham New Street', 'Manchester Piccadilly', 'Leeds City', 'Liverpool Lime Street',
  'Bristol Temple Meads', 'Newcastle Central', 'Edinburgh Waverley', 'Glasgow Central',
  'Cardiff Central', 'Sheffield Station', 'Nottingham Station', 'Leicester Station',
  'Coventry Station', 'Derby Station', 'York Station', 'Preston Station',
  'Crewe Station', 'Doncaster Station', 'Peterborough Station', 'Reading Station',
  'Oxford Station', 'Cambridge Station', 'Norwich Station', 'Southampton Central',
  'Brighton Station', 'Bournemouth Station', 'Exeter St Davids', 'Plymouth Station',
  'Stafford Station', 'Wolverhampton Station', 'Stoke-on-Trent Station', 'Chester Station'
];

// Hazard/Content codes
const HAZARD_CODES = ['UN1203', 'UN1202', 'UN1170', 'UN1223', 'UN1268', 'UN1863', 'UN1993', 'UN3295'];
const CONTENT_CODES = ['DIESEL', 'PETROL', 'ETHANOL', 'KEROSENE', 'CRUDE', 'JET-A1', 'BITUMEN', 'LPG'];

// Generate realistic train ID
const generateTrainId = () => {
  const prefixes = ['1A', '1B', '1C', '1D', '1E', '1F', '1G', '1H', '1J', '1K', '1L', '1M', '1N', '1P', '1S', '1T', '1V', '1W', '1Y', '1Z'];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const number = String(Math.floor(Math.random() * 99) + 1).padStart(2, '0');
  return `${prefix}${number}`;
};

// Generate wagon/container reference
const generateWagonRef = () => {
  const letters = 'ABCDEFGHJKLMNPRSTUVWXYZ';
  let ref = '';
  for (let i = 0; i < 4; i++) {
    ref += letters[Math.floor(Math.random() * letters.length)];
  }
  ref += String(Math.floor(Math.random() * 9000) + 1000);
  return ref;
};

// Generate weight (tonnes)
const generateWeight = () => {
  return (Math.floor(Math.random() * 50) + 10).toFixed(1);
};

// Generate time in HH:MM format
const generateTime = () => {
  const hour = String(Math.floor(Math.random() * 24)).padStart(2, '0');
  const minute = String(Math.floor(Math.random() * 60)).padStart(2, '0');
  return `${hour}:${minute}`;
};

// Generate date in DD/MM/YYYY format
const generateDate = () => {
  const day = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0');
  const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
  const year = 2025 + Math.floor(Math.random() * 2);
  return `${day}/${month}/${year}`;
};

// Apply a single-character or single-digit mutation for error generation
const applyMutation = (value, fieldType) => {
  const str = String(value);
  if (str.length === 0) return value;
  
  if (fieldType === 'number' || /^\d+(\.\d+)?$/.test(str)) {
    // Numeric mutation: change one digit
    const idx = Math.floor(Math.random() * str.replace('.', '').length);
    let actualIdx = idx;
    if (str.includes('.') && idx >= str.indexOf('.')) {
      actualIdx = idx + 1;
    }
    const chars = str.split('');
    const oldDigit = parseInt(chars[actualIdx]);
    let newDigit = (oldDigit + 1 + Math.floor(Math.random() * 8)) % 10;
    chars[actualIdx] = String(newDigit);
    return chars.join('');
  } else if (fieldType === 'time') {
    // Time mutation: change minute or hour digit
    const parts = str.split(':');
    if (Math.random() > 0.5) {
      parts[0] = String(Math.floor(Math.random() * 24)).padStart(2, '0');
    } else {
      parts[1] = String(Math.floor(Math.random() * 60)).padStart(2, '0');
    }
    return parts.join(':');
  } else if (fieldType === 'date') {
    // Date mutation: change day
    const parts = str.split('/');
    parts[0] = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0');
    return parts.join('/');
  } else {
    // String mutation: swap/change one character
    const idx = Math.floor(Math.random() * str.length);
    const chars = str.split('');
    if (/[A-Z]/.test(chars[idx])) {
      const letters = 'ABCDEFGHJKLMNPRSTUVWXYZ';
      chars[idx] = letters[Math.floor(Math.random() * letters.length)];
    } else if (/[0-9]/.test(chars[idx])) {
      chars[idx] = String(Math.floor(Math.random() * 10));
    } else {
      // For spaces or special chars, change adjacent
      const newIdx = Math.max(0, idx - 1);
      if (/[A-Za-z]/.test(chars[newIdx])) {
        chars[newIdx] = chars[newIdx] === chars[newIdx].toUpperCase() 
          ? chars[newIdx].toLowerCase() 
          : chars[newIdx].toUpperCase();
      }
    }
    return chars.join('');
  }
};

// ===== LEVEL CONFIGURATIONS =====

const LEVEL_CONFIGS = {
  1: {
    name: 'Basic Match',
    description: 'Compare source and comparison data. Answer Yes (Match) or No (Mismatch).',
    questionsCount: 10,
    timeLimit: null,
    fields: ['Origin', 'Destination', 'Train ID'],
    hasRowSelection: false,
    hasFieldSelection: false,
    hasKey: false,
  },
  2: {
    name: 'Error Field Identification',
    description: 'If data doesn\'t match, identify which field contains the error.',
    questionsCount: 20,
    timeLimit: null,
    fields: ['Origin', 'Destination', 'Train ID', 'Wagon Ref'],
    hasRowSelection: false,
    hasFieldSelection: true,
    hasKey: false,
  },
  3: {
    name: 'Key Application',
    description: 'Use the decoding key to verify consignment codes match their descriptions.',
    questionsCount: 30,
    timeLimit: null,
    fields: ['Consignment Code', 'Description', 'Hazard Code'],
    hasRowSelection: false,
    hasFieldSelection: true,
    hasKey: true,
  },
  4: {
    name: 'Multi-Row Data',
    description: 'Compare multiple rows. Identify which row and which field contains the error.',
    questionsCount: 40,
    timeLimit: null,
    fields: ['Origin', 'Destination', 'Train ID', 'Weight', 'Departure'],
    hasRowSelection: true,
    hasFieldSelection: true,
    hasKey: false,
    rowCount: 4,
  },
  5: {
    name: 'Full Simulation',
    description: '50 questions in 10 minutes. Complete test simulation.',
    questionsCount: 50,
    timeLimit: 600, // 10 minutes in seconds
    fields: ['Origin', 'Destination', 'Train ID', 'Wagon Ref', 'Weight', 'Departure'],
    hasRowSelection: true,
    hasFieldSelection: true,
    hasKey: true,
    rowCount: 5,
  },
};

// Key for Level 3 and 5 (consignment code decoding)
const CONSIGNMENT_KEY = {
  'A1': 'Hazardous Liquid',
  'A2': 'Hazardous Solid',
  'B1': 'Flammable Gas',
  'B2': 'Non-Flammable Gas',
  'C1': 'Corrosive Material',
  'C2': 'Oxidising Agent',
  'D1': 'Toxic Substance',
  'D2': 'Infectious Substance',
};

// ===== QUESTION GENERATOR =====

const generateQuestion = (level, questionIndex) => {
  const config = LEVEL_CONFIGS[level];
  const hasError = Math.random() > 0.4; // ~60% have errors
  
  // Generate source data
  const sourceData = {};
  const fields = config.fields;
  
  fields.forEach(field => {
    switch (field) {
      case 'Origin':
      case 'Destination':
        sourceData[field] = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];
        break;
      case 'Train ID':
        sourceData[field] = generateTrainId();
        break;
      case 'Wagon Ref':
        sourceData[field] = generateWagonRef();
        break;
      case 'Weight':
        sourceData[field] = generateWeight();
        break;
      case 'Departure':
        sourceData[field] = generateTime();
        break;
      case 'Consignment Code':
        const codes = Object.keys(CONSIGNMENT_KEY);
        sourceData[field] = codes[Math.floor(Math.random() * codes.length)];
        break;
      case 'Description':
        // Will be set based on consignment code
        break;
      case 'Hazard Code':
        sourceData[field] = HAZARD_CODES[Math.floor(Math.random() * HAZARD_CODES.length)];
        break;
      default:
        sourceData[field] = 'N/A';
    }
  });
  
  // For key-based levels, set description from key
  if (config.hasKey && sourceData['Consignment Code']) {
    sourceData['Description'] = CONSIGNMENT_KEY[sourceData['Consignment Code']];
  }
  
  // Generate comparison data (copy or with error)
  const comparisonData = { ...sourceData };
  let errorField = null;
  let errorRow = null;
  
  // For multi-row levels
  let sourceRows = [];
  let comparisonRows = [];
  
  if (config.rowCount) {
    // Generate multiple rows
    for (let i = 0; i < config.rowCount; i++) {
      const rowData = {};
      fields.forEach(field => {
        switch (field) {
          case 'Origin':
          case 'Destination':
            rowData[field] = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];
            break;
          case 'Train ID':
            rowData[field] = generateTrainId();
            break;
          case 'Wagon Ref':
            rowData[field] = generateWagonRef();
            break;
          case 'Weight':
            rowData[field] = generateWeight();
            break;
          case 'Departure':
            rowData[field] = generateTime();
            break;
          case 'Consignment Code':
            const codes = Object.keys(CONSIGNMENT_KEY);
            rowData[field] = codes[Math.floor(Math.random() * codes.length)];
            break;
          case 'Description':
            break;
          case 'Hazard Code':
            rowData[field] = HAZARD_CODES[Math.floor(Math.random() * HAZARD_CODES.length)];
            break;
          default:
            rowData[field] = 'N/A';
        }
      });
      if (config.hasKey && rowData['Consignment Code']) {
        rowData['Description'] = CONSIGNMENT_KEY[rowData['Consignment Code']];
      }
      sourceRows.push({ ...rowData });
      comparisonRows.push({ ...rowData });
    }
    
    // Introduce error in one row if hasError
    if (hasError) {
      errorRow = Math.floor(Math.random() * config.rowCount);
      const fieldsToMutate = fields.filter(f => f !== 'Description');
      errorField = fieldsToMutate[Math.floor(Math.random() * fieldsToMutate.length)];
      
      const fieldType = ['Weight', 'Departure'].includes(errorField) 
        ? (errorField === 'Departure' ? 'time' : 'number') 
        : 'string';
      
      comparisonRows[errorRow][errorField] = applyMutation(
        sourceRows[errorRow][errorField],
        fieldType
      );
    }
  } else {
    // Single row comparison
    if (hasError) {
      const fieldsToMutate = fields.filter(f => f !== 'Description');
      errorField = fieldsToMutate[Math.floor(Math.random() * fieldsToMutate.length)];
      
      const fieldType = ['Weight', 'Departure'].includes(errorField)
        ? (errorField === 'Departure' ? 'time' : 'number')
        : 'string';
      
      comparisonData[errorField] = applyMutation(sourceData[errorField], fieldType);
    }
  }
  
  return {
    id: questionIndex,
    sourceData: config.rowCount ? sourceRows : sourceData,
    comparisonData: config.rowCount ? comparisonRows : comparisonData,
    hasError,
    errorField,
    errorRow,
    fields,
    isMultiRow: !!config.rowCount,
    rowCount: config.rowCount || 1,
  };
};

// ===== MAIN COMPONENT =====

const DetailCheckingGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [score, setScore] = useState(0);
  const startTimeRef = useRef(null);
  
  const config = LEVEL_CONFIGS[level] || LEVEL_CONFIGS[1];
  
  // Generate questions on game start
  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && questions.length === 0) {
      const newQuestions = [];
      for (let i = 0; i < config.questionsCount; i++) {
        newQuestions.push(generateQuestion(level, i));
      }
      setQuestions(newQuestions);
      startTimeRef.current = Date.now();
      
      if (config.timeLimit) {
        setTimeLeft(config.timeLimit);
      }
    }
  }, [gameState, level, config.questionsCount, config.timeLimit, questions.length]);
  
  // Timer for Level 5
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING || !config.timeLimit) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          finishGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [gameState, config.timeLimit]);
  
  const question = questions[currentQuestion];
  
  const handleAnswerSubmit = () => {
    if (!question) return;
    
    const isCorrect = evaluateAnswer();
    
    setAnswers(prev => [...prev, {
      questionId: question.id,
      userAnswer: selectedAnswer,
      userField: selectedField,
      userRow: selectedRow,
      correct: isCorrect,
    }]);
    
    if (isCorrect) {
      setScore(s => s + 1);
      setFeedback({ type: 'correct', message: 'Correct!' });
    } else {
      let msg = 'Incorrect.';
      if (question.hasError) {
        if (question.isMultiRow) {
          msg = `Error was in Row ${question.errorRow + 1}, Field: ${question.errorField}`;
        } else {
          msg = `Error was in: ${question.errorField}`;
        }
      } else {
        msg = 'The data matched correctly.';
      }
      setFeedback({ type: 'wrong', message: msg });
    }
    
    setTimeout(() => {
      setFeedback(null);
      setSelectedAnswer(null);
      setSelectedField(null);
      setSelectedRow(null);
      
      if (currentQuestion + 1 >= config.questionsCount) {
        finishGame();
      } else {
        setCurrentQuestion(prev => prev + 1);
      }
    }, level === 5 ? 800 : 1500);
  };
  
  const evaluateAnswer = () => {
    if (!question) return false;
    
    // Level 1: Just Yes/No
    if (level === 1) {
      if (selectedAnswer === 'yes') return !question.hasError;
      if (selectedAnswer === 'no') return question.hasError;
      return false;
    }
    
    // Level 2+: Need to identify field if error exists
    if (!question.hasError) {
      return selectedAnswer === 'yes';
    }
    
    // There is an error - user must say no and identify field
    if (selectedAnswer !== 'no') return false;
    
    // Check field
    if (config.hasFieldSelection && selectedField !== question.errorField) return false;
    
    // Check row for multi-row levels
    if (config.hasRowSelection && question.isMultiRow) {
      if (selectedRow !== question.errorRow) return false;
    }
    
    return true;
  };
  
  const finishGame = useCallback(() => {
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const totalCorrect = answers.filter(a => a.correct).length + (feedback?.type === 'correct' ? 1 : 0);
    const totalQuestions = Math.min(currentQuestion + 1, config.questionsCount);
    const accuracy = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;
    
    setGameState(GAME_STATES.FINISHED);
    
    // Level 5 specific feedback
    let feedbackText = '';
    if (level === 5) {
      if (accuracy >= 90) {
        feedbackText = 'Excellent performance. You demonstrated high accuracy under time pressure.';
      } else if (accuracy >= 75) {
        feedbackText = 'Good performance. Minor improvements in speed or accuracy would help.';
      } else if (accuracy >= 60) {
        feedbackText = 'Satisfactory. Practice will improve your consistency.';
      } else {
        feedbackText = 'Below target. Focus on accuracy before speed.';
      }
    }
    
    onComplete({
      score: totalCorrect,
      maxScore: totalQuestions,
      accuracy,
      completionTime: timeSpent,
      feedbackText,
    });
  }, [answers, currentQuestion, config.questionsCount, feedback, level, onComplete]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // ===== RENDER READY STATE =====
  if (gameState === GAME_STATES.READY) {
    return (
      <DeviceModeSelector 
        onSelect={(mode) => {
          setDeviceMode(mode);
          setGameState(GAME_STATES.DEVICE_SELECT);
        }}
      />
    );
  }
  
  // ===== RENDER INSTRUCTIONS =====
  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#0B1D51]/10 rounded-full flex items-center justify-center">
              <FileSearch className="w-8 h-8 sm:w-10 sm:h-10 text-[#0B1D51]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Detail Checking Practice
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - {config.name}</p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3">How to Play:</h3>
            <p className="text-sm text-[#64748B] mb-3">{config.description}</p>
            <ol className="space-y-2 text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                Compare the Source Manifest with the Comparison Data
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                Check each field carefully for any differences
              </li>
              {level >= 2 && (
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">3.</span>
                  If there&apos;s an error, select the field containing the discrepancy
                </li>
              )}
              {level >= 4 && (
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">4.</span>
                  For multi-row data, also identify which row contains the error
                </li>
              )}
              {(level === 3 || level === 5) && (
                <li className="flex gap-2">
                  <span className="font-mono text-[#0B1D51]">{level === 3 ? '3' : '5'}.</span>
                  Use the Key to decode consignment codes
                </li>
              )}
            </ol>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div className="bg-white border rounded-lg p-3">
              <p className="text-[#64748B]">Questions</p>
              <p className="font-bold text-[#0B1D51]">{config.questionsCount}</p>
            </div>
            <div className="bg-white border rounded-lg p-3">
              <p className="text-[#64748B]">Time Limit</p>
              <p className="font-bold text-[#0B1D51]">
                {config.timeLimit ? formatTime(config.timeLimit) : 'None'}
              </p>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>Professional Assessment:</strong> This exercise develops detail checking and error detection skills used in manifest verification, consignment checking, and data comparison accuracy tests.
            </p>
          </div>
          
          <Button 
            onClick={() => {
              setGameState(GAME_STATES.PLAYING);
              startTimeRef.current = Date.now();
            }}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  // ===== RENDER PLAYING STATE =====
  if (gameState === GAME_STATES.PLAYING && question) {
    const canSubmit = selectedAnswer === 'yes' || 
      (selectedAnswer === 'no' && 
       (!config.hasFieldSelection || selectedField) &&
       (!config.hasRowSelection || !question.isMultiRow || selectedRow !== null));
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge variant="outline">
              Q{currentQuestion + 1}/{config.questionsCount}
            </Badge>
            <span className="text-sm text-[#64748B]">
              Score: {score}
            </span>
          </div>
          {config.timeLimit && (
            <div className={`flex items-center gap-2 font-mono text-lg ${timeLeft < 60 ? 'text-red-500' : 'text-[#0B1D51]'}`}>
              <Timer className="w-5 h-5" />
              {formatTime(timeLeft)}
            </div>
          )}
        </div>
        
        {/* Progress bar */}
        <Progress value={(currentQuestion / config.questionsCount) * 100} className="h-2" />
        
        {/* Key (for Levels 3 and 5) */}
        {config.hasKey && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="py-3">
              <p className="text-xs font-semibold text-amber-800 mb-2">CONSIGNMENT KEY:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {Object.entries(CONSIGNMENT_KEY).map(([code, desc]) => (
                  <div key={code} className="flex gap-1">
                    <span className="font-mono font-bold text-[#0B1D51]">{code}</span>
                    <span className="text-[#64748B]">= {desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Data Comparison Tables */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Source Manifest */}
          <Card className="border-[#0B1D51]/30">
            <CardContent className="py-3">
              <p className="text-xs font-semibold text-[#0B1D51] mb-2 flex items-center gap-1">
                <Table className="w-4 h-4" />
                SOURCE MANIFEST
              </p>
              {question.isMultiRow ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1 pr-2 text-[#64748B]">Row</th>
                        {question.fields.map(field => (
                          <th key={field} className="text-left py-1 px-2 text-[#64748B]">{field}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {question.sourceData.map((row, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-1 pr-2 font-mono text-[#0B1D51]">{idx + 1}</td>
                          {question.fields.map(field => (
                            <td key={field} className="py-1 px-2 font-mono">{row[field]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="space-y-2">
                  {question.fields.map(field => (
                    <div key={field} className="flex justify-between text-sm">
                      <span className="text-[#64748B]">{field}:</span>
                      <span className="font-mono text-[#0B1D51]">{question.sourceData[field]}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Comparison Data */}
          <Card className="border-slate-200">
            <CardContent className="py-3">
              <p className="text-xs font-semibold text-[#0B1D51] mb-2 flex items-center gap-1">
                <FileSearch className="w-4 h-4" />
                COMPARISON DATA
              </p>
              {question.isMultiRow ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1 pr-2 text-[#64748B]">Row</th>
                        {question.fields.map(field => (
                          <th key={field} className="text-left py-1 px-2 text-[#64748B]">{field}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {question.comparisonData.map((row, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-1 pr-2 font-mono text-[#0B1D51]">{idx + 1}</td>
                          {question.fields.map(field => (
                            <td key={field} className="py-1 px-2 font-mono">{row[field]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="space-y-2">
                  {question.fields.map(field => (
                    <div key={field} className="flex justify-between text-sm">
                      <span className="text-[#64748B]">{field}:</span>
                      <span className="font-mono text-[#0B1D51]">{question.comparisonData[field]}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Answer Section */}
        <Card className="border-slate-200">
          <CardContent className="py-4">
            <p className="text-sm font-semibold text-[#0B1D51] mb-3">
              Does the comparison data match the source manifest?
            </p>
            
            {/* Yes/No Buttons */}
            <div className="flex gap-3 mb-4">
              <Button
                variant={selectedAnswer === 'yes' ? 'default' : 'outline'}
                className={`flex-1 ${selectedAnswer === 'yes' ? 'bg-[#2CB67D] hover:bg-[#2CB67D]/90' : ''}`}
                onClick={() => {
                  setSelectedAnswer('yes');
                  setSelectedField(null);
                  setSelectedRow(null);
                }}
                disabled={!!feedback}
                data-testid="answer-yes-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Yes (Match)
              </Button>
              <Button
                variant={selectedAnswer === 'no' ? 'default' : 'outline'}
                className={`flex-1 ${selectedAnswer === 'no' ? 'bg-red-500 hover:bg-red-600' : ''}`}
                onClick={() => setSelectedAnswer('no')}
                disabled={!!feedback}
                data-testid="answer-no-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                No (Error Found)
              </Button>
            </div>
            
            {/* Field Selection (Level 2+) */}
            {selectedAnswer === 'no' && config.hasFieldSelection && (
              <div className="space-y-3">
                {/* Row Selection (Level 4+) */}
                {config.hasRowSelection && question.isMultiRow && (
                  <div>
                    <p className="text-xs text-[#64748B] mb-2">Which row contains the error?</p>
                    <Select value={selectedRow !== null ? String(selectedRow) : ''} onValueChange={(v) => setSelectedRow(parseInt(v))}>
                      <SelectTrigger className="w-full" data-testid="row-select">
                        <SelectValue placeholder="Select row..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: question.rowCount }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>Row {i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <p className="text-xs text-[#64748B] mb-2">Which field contains the error?</p>
                  <Select value={selectedField || ''} onValueChange={setSelectedField}>
                    <SelectTrigger className="w-full" data-testid="field-select">
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent>
                      {question.fields.filter(f => f !== 'Description').map(field => (
                        <SelectItem key={field} value={field}>{field}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Submit Button */}
            <Button
              onClick={handleAnswerSubmit}
              disabled={!canSubmit || !!feedback}
              className="w-full mt-4 bg-[#0B1D51] hover:bg-[#0B1D51]/90"
              data-testid="submit-answer-btn"
            >
              Submit Answer
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            {/* Feedback */}
            <AnimatePresence>
              {feedback && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`mt-4 p-3 rounded-lg text-center ${
                    feedback.type === 'correct' 
                      ? 'bg-[#2CB67D]/10 text-[#2CB67D] border border-[#2CB67D]/30' 
                      : 'bg-red-50 text-red-600 border border-red-200'
                  }`}
                >
                  <p className="font-medium">{feedback.message}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Loading state
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#0B1D51] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#64748B]">Loading questions...</p>
      </div>
    </div>
  );
};

export default DetailCheckingGame;
